/* eslint-disable max-lines */
import {
  Component, ChangeDetectionStrategy, Input, Output, EventEmitter, SimpleChanges, OnChanges
} from '@angular/core';

import { OptionSelected } from '@booking/entities/booking-arrangement';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { RANDOM_ID } from '@app/constants/general.constants';
import {
  DropDownFieldEvent, DropDownFieldType
} from '@design-system/components/templates/drop-down-field/entities/drop-down-field.types';
import {
  DropDownListOption, DropDownListSelectedBy, DropDownListType
} from '@design-system/components/templates/drop-down-list/entities/drop-down-list.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { InputTextFailedType } from '@design-system/components/molecules/input-text/entities/input-text.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { SwipeBorderRadiusDs } from '@design-system/entities/border-radius.type';

@Component({
  selector: 'app-dropdown-select',
  templateUrl: './dropdown-select.component.html',
  styleUrls: ['./dropdown-select.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DropdownSelectComponent implements OnChanges {

  @Input() id: string = '';
  @Input() title: string;
  @Input() value: string | null = null;
  @Input() position: string;
  @Input() inputPlaceholder = '';
  @Input() inputIsFilled = false;
  @Input() inputLabel = '';
  @Input() defaultSelection = '-'
  @Input() optionIconClass: string = '';
  @Input() showError: boolean;
  @Input() showTitle: boolean = true;
  @Input() isAirline: boolean = false;
  @Input() fullWidth: boolean = false;
  @Input() fullHeight: boolean = false;
  @Input() scrollToMiddle: boolean = false;
  @Input() showOptions: boolean = false;
  @Input() inSwipeUp: boolean = false;
  @Input() withSearchInput: boolean = false;
  @Input() inChildrenAges: boolean = false;
  @Input() disabledOptionList: boolean = false;
  @Input() disabledPagination: boolean = false;
  @Input() scrollFixedPosition: boolean = true;
  @Input() manipulateDOM: boolean = true;
  @Input() selectOptions: string[] = [];
  @Input() defaultOption: string;
  @Input() orderFirstSelected: boolean;
  @Input() isMobile: boolean;
  @Input() icon: Icon;

  @Output() showOptionsEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() selected: EventEmitter<OptionSelected> = new EventEmitter<OptionSelected>();

  private selectionActivate: boolean = false;
  private filteredOptionsList: string[] = [];

  public valueInput: string = '';
  public uniqueIdentificator: number = Math.floor(Math.random() * RANDOM_ID);
  public dropDownClass: string = `dropdown-${this.uniqueIdentificator}`;
  public mobileDropDownContainerScroll: string = `.${this.dropDownClass}`;
  public sliceOptions: number = 0;

  public dropDownListSelected: DropDownListOption;

  public readonly fieldFailedType = InputTextFailedType.Warning;

  public readonly swipeBorderRadius = SwipeBorderRadiusDs;

  public readonly dropDownListType = DropDownListType;
  public readonly dropDownListSelectedBy = DropDownListSelectedBy;

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  constructor(
    private readonly domManipulateService: DomManipulateService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.defaultOption && this.dropDownListSelected?.Title !== changes.defaultOption.currentValue) {
      const selected = this.dropDownDsOptions?.find(option => option?.Title === changes.defaultOption.currentValue);
      this.dropDownListSelected = selected;
      this.defaultOption = selected?.Title;
    }
  }

  get dropDownFieldType(): DropDownFieldType {
    return this.isMobile ? DropDownFieldType.Field : DropDownFieldType.FieldList;
  }

  get dropDownDsOptions(): DropDownListOption[] {
    return this.optionList.map((option, index) => ({
      Id: index.toString(),
      Title: option,
      Icon: this.icon,
    }));
  }

  get hasOptionIconClass(): boolean {
    return !!this.optionIconClass;
  }

  get optionSelected(): string | null {
    return this.value ? this.value : this.defaultOption ? this.defaultOption : this.defaultSelection;
  }

  get text(): string {
    return this.valueInput.trimRight();
  }

  get isActivateInput(): boolean {
    return !!this.text;
  }

  get infiniteScrollDisabled(): boolean {
    return !this.showOptions || !!this.disabledPagination;
  }

  get selectOptionsList(): string[] {
    if (this.optionSelected && this.orderFirstSelected) {
      const selectOptionsWithOutSelected = this.selectOptions.filter(x => x !== this.optionSelected);
      return [this.optionSelected, ...selectOptionsWithOutSelected];
    } else {
      return this.selectOptions;
    }
  }

  get optionList(): string[] {
    return this.isActivateInput ? this.filteredOptionsList : this.selectOptionsList;
  }

  public toggleShowOptions(): void {
    if (!this.disabledOptionList) {
      this.showOptions = !this.showOptions;
      this.showOptionsEmitter.emit(this.showOptions);
      this.setDomManipulation();
    }
  }

  public toggleInputOptions(): void {
    if (!this.isMobile && !this.showOptions && !this.selectionActivate) {
      this.toggleShowOptions();
    } else if (this.selectionActivate) {
      this.selectionActivate = false;
    }
  }

  public selectDropDownFieldDsEvent(selected: DropDownFieldEvent): void {
    this.selectDropDownDsEvent(selected?.Option);
  }

  public selectDropDownDsEvent(option: DropDownListOption): void {
    this.defaultOption = option?.Title;
    this.dropDownListSelected = option;
    this.selected.emit({ value: this.defaultOption, index: Number(option?.Id) });
    this.toggleShowOptions();
  }

  private setDomManipulation(): void {
    if (this.isMobile && !this.inChildrenAges && this.manipulateDOM) {
      this.domManipulateService.setBodyScroll(!this.showOptions);
    }
  }

}
